import { parseCharacters } from "@/utils/format";
import {
  collectDatacenterValuesForFieldData,
  getNormalizedFieldType,
  buildFieldConfiguration,
  validateLocalListItemsRequired,
  validateDataCenterRequired,
  validateFileTypesRequired,
  validateGenericRequired,
  validateCustomRules,
} from "@/pages/digital-files/utils/field-form";
import type {
  EntityFieldType,
  FieldConfig,
  FieldTypeConfiguration,
} from "../config/type";
import type { EntityField } from "@/pages/digital-files/types";

/**
 * Builds initial dynamic values from fieldConfig with default values from initialField
 */
export const buildInitialDynamicValues = (
  initialField: EntityField,
  fieldConfig: FieldTypeConfiguration | null | undefined,
): Record<string, unknown> => {
  const dynamicValues: Record<string, unknown> = {};
  fieldConfig?.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.key !== "fieldName") {
        const value = initialField[field.key];
        dynamicValues[field.key] =
          value === undefined ? field.defaultValue : value;
      }
    });
  });
  return dynamicValues;
};

/**
 * Sets up datacenter field values (options_table, options_column, etc.)
 */
export const setupDatacenterFields = (
  initialField: EntityField,
  dynamicValues: Record<string, unknown>,
  fieldConfig: FieldTypeConfiguration | null | undefined,
): void => {
  fieldConfig?.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.type !== "datacenter") return;

      const tableKey = `${field.key}_table`;
      const columnKey = `${field.key}_column`;

      if (field.key === "valueDatacenter") {
        if (initialField.options_table && initialField.options_column) {
          dynamicValues[tableKey] = initialField.options_table;
          dynamicValues[columnKey] = initialField.options_column;
        }
      } else if (field.key === "options_root_datacenter") {
        if (
          initialField.options_root_table &&
          initialField.options_root_column
        ) {
          dynamicValues[tableKey] = initialField.options_root_table;
          dynamicValues[columnKey] = initialField.options_root_column;
        }
      } else if (initialField[tableKey] && initialField[columnKey]) {
        dynamicValues[tableKey] = initialField[tableKey];
        dynamicValues[columnKey] = initialField[columnKey];
      }
    });
  });
};

/**
 * Sets up file type configuration
 */
export const setupFileTypeConfiguration = (
  initialField: EntityField,
  dynamicValues: Record<string, unknown>,
): void => {
  if (initialField.allowedFileTypes === undefined) return;

  const fileTypes = Array.isArray(initialField.allowedFileTypes)
    ? initialField.allowedFileTypes
    : [];
  dynamicValues.allowAnyFileType = fileTypes.length === 0;
};

/**
 * Sets up options root configuration (manual or database)
 */
export const setupOptionsRootConfiguration = (
  initialField: EntityField,
  dynamicValues: Record<string, unknown>,
  fieldConfig: FieldTypeConfiguration | null | undefined,
): void => {
  const hasOptionsRootConfig =
    initialField.options_root_table ||
    initialField.options_root_source === "manual";

  if (
    !hasOptionsRootConfig ||
    !initialField.options ||
    !Array.isArray(initialField.options)
  ) {
    return;
  }

  fieldConfig?.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.type === "local_list" && field.key === "options_root_list") {
        dynamicValues[field.key] = initialField.options;
      }
    });
  });
};

/**
 * Sets up dropdown button configuration
 */
export const setupDropdownButtonConfiguration = (
  initialField: EntityField,
  dynamicValues: Record<string, unknown>,
  fieldConfig: FieldTypeConfiguration | null | undefined,
  setLocalListItems: (items: string[]) => void,
): void => {
  if (
    !initialField.options_dropdown ||
    !Array.isArray(initialField.options_dropdown)
  ) {
    return;
  }

  setLocalListItems(initialField.options_dropdown);
  fieldConfig?.sections.forEach((section) => {
    section.fields.forEach((field) => {
      if (field.type === "local_list" && field.key === "localList") {
        dynamicValues[field.key] = initialField.options_dropdown;
      }
    });
  });
};

/**
 * Sets up identification field special handling
 */
export const setupIdentificationField = (
  initialField: EntityField,
  dynamicValues: Record<string, unknown>,
  type: EntityFieldType | null,
): void => {
  if (type !== "identification") return;

  if (initialField.documentType === "auto") {
    dynamicValues.allowUserChooseDocType = true;
    dynamicValues.documentType = "";
  }
};

/**
 * Sets up source configuration (valueSource, options_root_source)
 */
export const setupSourceConfiguration = (
  initialField: EntityField,
  dynamicValues: Record<string, unknown>,
): void => {
  if (initialField.options_source) {
    dynamicValues.valueSource = initialField.options_source;
  }

  if (initialField.options_root_source) {
    dynamicValues.options_root_source = initialField.options_root_source;
  } else if (initialField.options_root_table) {
    dynamicValues.options_root_source = "database";
  } else if (
    initialField.options &&
    Array.isArray(initialField.options) &&
    initialField.options.length > 0 &&
    !initialField.options_table
  ) {
    dynamicValues.options_root_source = "manual";
  }

  if (initialField.buttonEnabled !== undefined) {
    dynamicValues.hasDropdownButton = initialField.buttonEnabled;
  }
};

export const hasRequiredValidationError = (
  field: FieldConfig,
  value: unknown,
  formValues: Record<string, unknown>,
  localListItems: string[],
): boolean => {
  if (field.type === "switch") {
    return false;
  }

  const tempErrors: Record<string, string> = {};

  if (field.type === "local_list") {
    validateLocalListItemsRequired({
      field,
      value,
      formValues,
      localListItems,
      newErrors: tempErrors,
    });
  } else if (field.type === "file_types") {
    validateFileTypesRequired(field, value, tempErrors);
  } else if (field.type === "datacenter") {
    validateDataCenterRequired({
      field,
      formValues,
      newErrors: tempErrors,
    });
  } else {
    validateGenericRequired(field, value, tempErrors);
  }

  return Boolean(tempErrors[field.key]);
};

export const manageFieldValidation = (
  field: FieldConfig,
  newErrors: Record<string, string>,
  formValues: Record<string, unknown>,
  localListItems: string[],
) => {
  // fieldName se maneja por separado, ya lo validamos arriba
  if (field.key === "fieldName") {
    return;
  }

  // Verificar si el campo debe mostrarse según sus condiciones
  const isVisible = !field.condition || field.condition(formValues);

  if (isVisible && field.required) {
    const value = formValues[field.key];

    // Para switches, siempre son válidos (tienen valor booleano por defecto)
    if (field.type === "switch") {
      // Los switches siempre tienen un valor (true o false), así que son válidos
      // No necesitamos validar más
    }
    // Para listas locales, validar según el tipo y source
    else if (field.type === "local_list") {
      validateLocalListItemsRequired({
        field,
        value,
        formValues,
        localListItems,
        newErrors,
      });
    }
    // Para file_types, verificar que tengan items
    else if (field.type === "file_types") {
      validateFileTypesRequired(field, value, newErrors);
    }
    // Para centro de datos, validar según el source correspondiente
    else if (field.type === "datacenter") {
      validateDataCenterRequired({ field, formValues, newErrors });
    }

    // Para otros campos, verificar que tengan valor
    // Si el campo tiene defaultValue, considerar que está bien incluso si es string vacío
    // Solo marcar como inválido si es undefined o null (no inicializado)
    else {
      validateGenericRequired(field, value, newErrors);
    }

    // Validaciones personalizadas (solo si el campo tiene valor)
    validateCustomRules(field, value, newErrors);
  }
};

type BuildFieldDataParams = {
  type: EntityFieldType | null;
  fieldName: string;
  fieldConfig: FieldTypeConfiguration | null | undefined;
  formValues: Record<string, unknown>;
  fieldId: string;
  generateId: () => string;
  keyValue: string;
  isArray: boolean;
  allowedQuantityOfValues: string;
  localListItems: string[];
};

// Función para obtener los datos del campo listos para guardar
export const buildFieldData = ({
  type,
  fieldName,
  fieldConfig,
  formValues,
  fieldId,
  generateId,
  keyValue,
  isArray,
  allowedQuantityOfValues,
  localListItems,
}: BuildFieldDataParams): EntityField | null => {
  if (!type || !fieldName.trim()) return null;

  // Recoger todos los valores de datacenter, separando options_* de options_root_*
  const {
    datacenterValues,
    optionsTable,
    optionsColumn,
    optionsRootTable,
    optionsRootColumn,
  } = collectDatacenterValuesForFieldData(fieldConfig, formValues);

  // Separar propiedades especiales que se manejan explícitamente
  const {
    required: requiredValue,
    configuration: formConfiguration,
    allowUserChooseDocType,
    documentType,
    options_root_list,
    options_root_source,
    valueSource,
    hasDropdownButton,
    buttonPosition,
    ...restFormValues
  } = formValues;

  // Remover los valores de datacenter de restFormValues
  Object.keys(datacenterValues).forEach((key) => {
    delete restFormValues[key];
  });

  // Para campos de identificación: si allowUserChooseDocType es true, usar "auto"
  const finalDocumentType =
    type === "identification" && allowUserChooseDocType ? "auto" : documentType;

  const normalizedType = getNormalizedFieldType(type);

  const configuration = buildFieldConfiguration({
    formConfiguration,
    normalizedType,
    currentType: type,
    finalDocumentType,
    hasDropdownButton,
    buttonPosition,
    valueSource,
    optionsTable,
    optionsColumn,
    optionsRootSource: options_root_source,
    optionsRootTable,
    optionsRootColumn,
    optionsRootList: options_root_list,
    datacenterValues,
    localListItems,
  });

  const fieldData: EntityField = {
    ...restFormValues,
    id: fieldId || generateId(),
    type: normalizedType,
    name: fieldName,
    key: keyValue || parseCharacters(fieldName.toLowerCase()),
    required: typeof requiredValue === "boolean" ? requiredValue : false,
    is_array: isArray,
    max_items: allowedQuantityOfValues
      ? Number.parseInt(allowedQuantityOfValues)
      : null,
    configuration,
  };

  return fieldData;
};
